/**
 * File Name          : datasnap-analysis.service.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Injectable } from '@angular/core';
import { ParsedValues } from '../../../projects/idl-physens/src/lib/utilities/physens-parser';
import { ImpactAnalysisFunction } from '../lesson-utils/impact-analysis';
import { AirRacerAnalysisFunction } from '../lesson-utils/air_racer-analysis';
import { v4 as uuidv4 } from 'uuid';
import { ProjectileAnalysisFunction } from '../lesson-utils/projectile-analysis';

@Injectable({
  providedIn: 'root'
})
export class DatasnapAnalysisService {
  private worker: Worker;
  private ready!: Promise<void>;
  private scopeReady = new Map<string, { promise: Promise<void>, resolve: () => void, reject: (e: any) => void }>();

  constructor() {
    this.worker = new Worker(
      new URL('../workers/pyodide.worker', import.meta.url),
      { type: 'classic' }
    );

    this.setReady();
  }

  setReady() { 
    this.ready = new Promise((resolve, reject) => {
      this.worker.onmessage = (event) => {
        const { type, message, error, scopeId, uuid } = event.data;
        if (type === 'status') console.log('[Pyodide Worker]', message);
        if (type === 'ready') resolve();
        if (type === 'scopeReady') this.scopeReady.get(scopeId)?.resolve();
        if (type === 'error' && scopeId && !uuid) {
          console.error("Analysis scope init failed:", scopeId, error);
          this.scopeReady.get(scopeId)?.reject(error);
          this.scopeReady.delete(scopeId);
        } else if (type === 'error' && !uuid) {
          console.log("hit worker init error: ", error)
          reject(error);
        }
        if (type === 'unloaded') {
          console.log("Worker unloaded Pyodide, reloading...");
          this.setReady();
        }
        if (type === 'log') console.log('[Pyodide]', message);
        if (type === 'logerror') console.error('[Pyodide ERROR]', message);
      };
    });
  }

  private async postAndWait<T>(message: any): Promise<T> {
    await this.ready;
    return new Promise<T>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        const { type, uuid, result, error } = event.data;
        if(uuid != message.uuid) return;
        if (type === 'result') {
          this.worker.removeEventListener('message', listener);
          resolve(result);
        } else if (type === 'error') {
          this.worker.removeEventListener('message', listener);
          reject(error);
        }
      };
      this.worker.addEventListener('message', listener);
      this.worker.postMessage(message);
    });
  }

  async runPython(code: string): Promise<any> {
    return this.postAndWait({ type: 'runPython', code });
  }

  async callPythonFunction(funcName: string, scopeId: string, args: any[] = []): Promise<any> {
    const uuid = uuidv4()
    return this.postAndWait({ type: 'callFunction', uuid, funcName, args, scopeId });
  }

  async runAnalysis(unitId: string, data: ParsedValues) {
    let funcName = 'NotARealFunction';
    switch (unitId) {
      case 'impact':
        funcName = 'ImpactAnalysis';
        break;
      case 'air_racer':
        funcName = 'AirRacerAnalysis';
        break;
      case 'projectile':
        funcName = 'ProjectileAnalysis';
        break;
      default:
        return;
    }
    // The worker reports 'ready' before the analysis scripts are loaded, so wait for this scope's own signal.
    if (!this.scopeReady.has(unitId)) {
      await this.prepareScopes(unitId);
    }
    await this.scopeReady.get(unitId)!.promise;
    return this.callPythonFunction(funcName, unitId, [data]);
  }

  async prepareScopes(unitId: string) {
    const scripts: Record<string, string> = {
      impact: ImpactAnalysisFunction,
      air_racer: AirRacerAnalysisFunction,
      projectile: ProjectileAnalysisFunction,
    };
    const script = scripts[unitId];
    if (!script) return;

    let resolve!: () => void;
    let reject!: (e: any) => void;
    const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
    promise.catch(() => undefined);
    this.scopeReady.set(unitId, { promise, resolve, reject });

    this.worker.postMessage({ type: 'init', scopeId: unitId, scripts: [script] });
  }
}
